import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../button/button.component';
import { OverlayContainer } from '@angular/cdk/overlay';
import { TooltipControlService } from '../../services/tooltip-control.service';

export type ModalWidth = 'small' | 'large' | 'extra-large' | 'super-large';

@Component({
  selector: 'work-ui-modal',
  standalone: true,
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  imports: [TranslateModule, ButtonComponent],
})
export class ModalComponent implements OnInit, OnDestroy {
  static PREVIOUSLY_ACTIVE_MODAL: HTMLElement | undefined;
  static CURRENTLY_ACTIVE_MODAL: HTMLElement | undefined;
  @ViewChild('modalBody') modalBody!: ElementRef;
  @ViewChild('modalCard') modalCard!: ElementRef;
  @ViewChild('dynamicComponentSlot', { read: ViewContainerRef }) dynamicComponentSlot!: ViewContainerRef;
  @Input({ required: true }) modalId = 'workModal';
  @Input() titleText = '';
  @Input() introText = '';
  @Input() showInlineBorders = false;
  @Input() modalWidth: ModalWidth | undefined;
  @Input() hideCloseButton = false;
  @Input() scrollableBody = false;
  @Input() noBodyPadding = false;
  @Input() noBodyPaddingHorizontal = false;
  @Input() noFooter = false;
  @Input() alignedTop = false;
  @Input() anchoredTop = false; // prevents layout shifts, when the height of the modal changes
  @Input() closableOnOutsideClick = false;
  @Input() dynamicComponent: ComponentRef<any> | undefined;
  @Input() askForConfirmationOnClose = false;
  /**
   * Optional callback to confirm before closing.
   * When provided and `askForConfirmationOnClose` is true, this will be called on user-initiated close.
   * Should return `true` to allow closing, `false` to prevent it.
   */
  @Input() confirmCloseCallback?: () => Promise<boolean>;
  @Output() modalClosedWithNoButton: EventEmitter<void> = new EventEmitter<void>();
  @Output() modalClosed: EventEmitter<void> = new EventEmitter<void>();
  public isOpen = false;
  public isOpenWithAnimationDelay = false;
  public noBodyContent = false;
  public modalElement!: HTMLElement;
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private overlayContainer = inject(OverlayContainer);
  private tooltipControlService = inject(TooltipControlService);
  private platformId = inject(PLATFORM_ID);
  private lastFocusedElement!: HTMLElement;
  private onlyOneModalOpen = true;
  private animationDelay = 500;
  private isClosingConfirmed = false;
  // function returned by Angular renderer, function which unsubscribes on call
  private modalKeydownListener: (() => void) | null = null;
  // function returned by Angular renderer, function which unsubscribes on call
  private bodyKeydownListener: (() => void) | null = null;

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      if (!this.modalElement) {
        this.modalElement = this.elementRef.nativeElement.querySelector('.work-ui-modal');
      }
      if (!this.isOpen) {
        this.modalElement.style.display = 'none';
      }
    }
  }

  ngOnDestroy() {
    if (this.isOpen && this.onlyOneModalOpen && isPlatformBrowser(this.platformId)) {
      this.renderer.setStyle(document.documentElement, 'overflow-y', 'auto');
    }
    if (this.dynamicComponent) {
      this.dynamicComponent.destroy();
    }
  }

  @HostListener('document:click', ['$event.target'])
  public async onDocumentClick(targetElement: HTMLElement) {
    if (!this.closableOnOutsideClick || !this.isOpen) {
      return;
    }
    // check if click happened in blurry background area of modal
    if (targetElement.classList.contains('work-ui-modal') || targetElement.classList.contains('work-ui-modal__container')) {
      await this.close(false);
    }
  }

  /**
   * Show the modal
   */
  public open(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.modalElement = this.elementRef.nativeElement.querySelector('.work-ui-modal');
      ModalComponent.PREVIOUSLY_ACTIVE_MODAL = ModalComponent.CURRENTLY_ACTIVE_MODAL;
      ModalComponent.CURRENTLY_ACTIVE_MODAL = this.modalElement;
      this.renderer.setStyle(document.documentElement, 'overflow-y', 'hidden');
      this.lastFocusedElement = document.activeElement as HTMLElement;
      this.isOpen = true;
      this.modalElement.style.pointerEvents = 'auto';
      this.isOpenWithAnimationDelay = true;
      this.changeDetectorRef.detectChanges();
      this.modalElement.style.display = 'flex';
      this.modalKeydownListener = this.renderer.listen(this.modalElement, 'keydown', this.trapFocusInsideModal.bind(this));
      this.bodyKeydownListener = this.renderer.listen('body', 'keydown', this.closeModalOnEscape.bind(this));
      if (this.dynamicComponent) {
        this.insertDynamicComponent(this.dynamicComponent);
      }
      this.focusFirstElement();

      // check if body has content
      setTimeout(() => {
        if (this.modalBody.nativeElement.children.length === 0) {
          this.noBodyContent = true;
        }
      }, 0);
    }
  }

  /**
   * close the modal
   * @param userInitiatedClose true when closed via cancel button, outside click, or escape key (can trigger confirmation)
   */
  public async close(userInitiatedClose?: boolean): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      // Check if confirmation is required and this is a user-initiated close
      if (this.askForConfirmationOnClose && userInitiatedClose && !this.isClosingConfirmed) {
        if (this.confirmCloseCallback) {
          const userConfirmed = await this.confirmCloseCallback();
          if (!userConfirmed) {
            return;
          }
        }
        this.isClosingConfirmed = true;
      }

      ModalComponent.CURRENTLY_ACTIVE_MODAL = ModalComponent.PREVIOUSLY_ACTIVE_MODAL;

      this.isOpenWithAnimationDelay = false;
      this.onlyOneModalOpen = document.querySelectorAll('.work-ui-modal.open').length === 1;

      if (this.onlyOneModalOpen) {
        this.renderer.removeStyle(document.body, 'overflow');
        if (userInitiatedClose) {
          this.modalClosedWithNoButton.emit();
        }
        this.renderer.setStyle(document.documentElement, 'overflow-y', 'auto');
      }

      this.modalElement.style.pointerEvents = 'none';

      this.unsubscribeListeners();
      this.modalClosed.emit();

      this.isClosingConfirmed = false;

      // Keep the modal flagged open (and its body content mounted) until the close animation
      // has fully played out — flipping `isOpen` synchronously unmounts the `@if (modal.isOpen)`
      // body before the collapse animation finishes, which makes the modal look like it empties
      // and then collapses.
      await new Promise(resolve => {
        setTimeout(() => {
          try {
            this.isOpen = false;
            if (this.modalElement) {
              this.modalElement.style.display = 'none';
            }
            if (this.lastFocusedElement) {
              this.tooltipControlService.temporarilyDisableTooltips();
              this.lastFocusedElement.focus();
            }
          } catch (error) {
            console.error('Error in modal timeout:', error);
          } finally {
            resolve(undefined);
          }
        }, this.animationDelay);
      });
    }
  }

  private unsubscribeListeners() {
    if (this.modalKeydownListener) {
      this.modalKeydownListener();
    }
    if (this.bodyKeydownListener) {
      this.bodyKeydownListener();
    }
  }

  /**
   * Trap focus inside open modal for accessibility, handle closing on escape key press
   * @param event
   * @private
   */
  private trapFocusInsideModal(event: KeyboardEvent): void {
    if (isPlatformBrowser(this.platformId)) {
      if (event.key === 'Tab') {
        const focusableElements = this.elementRef.nativeElement.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  }

  private closeModalOnEscape(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isOpen && ModalComponent.CURRENTLY_ACTIVE_MODAL === this.modalElement) {
      event.stopPropagation();
      this.close(true).then();
    }
  }

  /**
   * Automatically focus first input element after opening the modal (ignore close button)
   * @private
   */
  private focusFirstElement(): void {
    if (isPlatformBrowser(this.platformId)) {
      const focusableElements = this.elementRef.nativeElement.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusableElements.length) {
        // ignore close icon button, therefore [1]
        if (focusableElements[1]) {
          focusableElements[1].focus();
        }
      }
    }
  }

  private insertDynamicComponent(componentRef: ComponentRef<any>): void {
    this.dynamicComponent = componentRef;
    this.dynamicComponentSlot.clear();
    this.dynamicComponentSlot.insert(componentRef.hostView);
  }

  /* close potentially open tooltips or similar overlays */
  private closeAllOverlays() {
    const overlayContainerElement = this.overlayContainer.getContainerElement();
    // Clear all the child elements inside the overlay container
    while (overlayContainerElement.firstChild) {
      overlayContainerElement.removeChild(overlayContainerElement.firstChild);
    }
  }
}
